/* eslint-disable no-console */
/* eslint-disable max-lines */
'use strict'

export class IndexController {
  private static usersCounter(): void {
    const selector = '.numCounter'
    const initialDelay = 100

    class Counter {
      private settings = {
        digits: 5,
        delay: 250,
        direction: ''
      }

      private DOM = {
        scope: document.querySelector(selector),
        digits: [] as HTMLElement[]
      }

      private value = 0

      constructor() {
        this.settings = {
          ...this.settings,
          ...{
            digits: 7,
            delay: 200,
            direction: 'rtl'
          }
        }

        this.DOM.scope.innerHTML =
          '<div class="value-container"><b data-value="0"></b></div>'.repeat(
            this.settings.digits
          )

        this.DOM.digits = Array.from(this.DOM.scope.querySelectorAll('b'))
        this.DOM.scope.addEventListener(
          'transitionend',
          this.handleTransitionEnd.bind(this)
        )

        this.count()
      }

      private handleTransitionEnd = (e: TransitionEvent): void => {
        if (e.pseudoElement === '::before' && e.propertyName === 'margin-top') {
          ;(e.target as HTMLElement).classList.remove('blur')
        }
      }

      count = (newVal?: number): void => {
        const countTo = (newVal || this.value).toString().split('').reverse()
        const digitsElms =
          this.settings.direction === 'rtl'
            ? [...this.DOM.digits].reverse()
            : this.DOM.digits

        digitsElms.forEach((item, i) => {
          const diff = Math.abs(+countTo[i] - +item.dataset.value)
          setTimeout(() => {
            item.dataset.value = countTo[i]
            if (diff > 3) {
              item.className = 'blur'
            }
          }, i * this.settings.delay)
        })
      }
    }

    const initializeCounter = (): void => {
      const initialValue = (): number => {
        const startTimestamp = new Date('2024-01-01T00:00:00Z').getTime()
        const currentTimestamp = new Date().getTime()
        const secondsPassed = Math.floor(
          (currentTimestamp - startTimestamp) / 1000
        )
        const secondsInYear =
          new Date().getFullYear() % 2 === 0 ? 31622400 : 31536000
        const usagePerMonth = 60000 * 60
        const growthFactor = 1.8

        return Math.round(
          usagePerMonth * Math.pow(growthFactor, secondsPassed / secondsInYear)
        )
      }

      const updateCounterValue = (counterInstance: Counter): void => {
        counterInstance.count(initialValue())
      }

      const counter = new Counter()
      updateCounterValue(counter)

      setInterval(() => updateCounterValue(counter), initialDelay)
    }

    initializeCounter()
  }

  private static adaptiveLazyLoadImages(): void {
    const imagesWithSrcSet = document.querySelectorAll(
      '[data-src-small], [data-src-medium], [data-src-large]'
    )

    const observer = new IntersectionObserver((entries, observer) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const image = entry.target
          const isRetina = window.matchMedia(
            '(-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi)'
          ).matches

          const dataSrcSmall = image.getAttribute('data-src-small')
          const dataSrcMedium = image.getAttribute('data-src-medium')
          const dataSrcLarge = image.getAttribute('data-src-large')

          if (!isRetina) {
            // Non-retina displays
            if (window.innerWidth < 768) {
              image.setAttribute('src', dataSrcSmall || '')
            } else if (window.innerWidth < 1200) {
              image.setAttribute('src', dataSrcMedium || '')
            } else {
              image.setAttribute('src', dataSrcLarge || '')
            }
          } else {
            // Retina displays
            if (window.innerWidth < 768) {
              image.setAttribute('src', dataSrcMedium || '')
            } else if (window.innerWidth < 1200) {
              image.setAttribute('src', dataSrcLarge || '')
            } else {
              image.setAttribute('src', dataSrcLarge || '')
            }
          }
          observer.unobserve(entry.target)
        }
      })
    })

    imagesWithSrcSet.forEach(image => {
      observer.observe(image)
    })

    window.addEventListener('load', () => {
      imagesWithSrcSet.forEach(image => {
        if (image.getBoundingClientRect().top < window.innerHeight) {
          observer.unobserve(image)
          image.dispatchEvent(new Event('intersect'))
        }
      })
    })

    window.addEventListener('resize', () => {
      imagesWithSrcSet.forEach(image => {
        if (image.getBoundingClientRect().top < window.innerHeight) {
          observer.unobserve(image)
          image.dispatchEvent(new Event('intersect'))
        }
      })
    })
  }

  static handleVideosPlaybackTrack(): void {
    const options: IntersectionObserverInit = {
      root: null,
      rootMargin: '0px',
      threshold: 0.5
    }

    const setProgress = (
      progress: number,
      progressCircle: HTMLElement
    ): void => {
      const circumference: number = 2 * Math.PI * 21 // Radius is 21
      const offset: number = circumference * (1 - progress / 100)

      if (progress === 100) {
        progressCircle.style.transition = 'none'
      } else if (progress === 1) {
        progressCircle.style.transition = 'stroke-dashoffset 0.24s ease-in-out'
      }

      progressCircle.style.strokeDashoffset = offset.toString()
    }

    const togglePlay = (
      video: HTMLVideoElement,
      playIcon: HTMLElement,
      pauseIcon: HTMLElement
    ): void => {
      if (video.paused) {
        video.play()
        playIcon.style.display = 'block'
        pauseIcon.style.display = 'none'
        // console.log(`Video ${video.id} started playing.`);
      } else {
        video.pause()
        playIcon.style.display = 'none'
        pauseIcon.style.display = 'block'
        // console.log(`Video ${video.id} paused.`);
      }
      setProgress(
        (video.currentTime / video.duration) * 100,
        video.parentElement.querySelector('.progress')
      )
    }

    const trackPlayback = (video: HTMLVideoElement): void => {
      const percentagePlayed: number =
        (video.currentTime / video.duration) * 100
      if (!isNaN(percentagePlayed) && !video.paused) {
        setProgress(
          percentagePlayed,
          video.parentElement.querySelector('.progress')
        )
      }
    }

    const callback = (entry: IntersectionObserverEntry): void => {
      const video: HTMLVideoElement = entry.target as HTMLVideoElement
      if (entry.isIntersecting) {
        video.play()
        // console.log(`Video ${video.id} entered the viewport and started playing.`);
      } else {
        video.pause()
        // console.log(`Video ${video.id} exited the viewport and paused.`);
      }
    }

    const observeVideo = (videoContainer: HTMLElement): void => {
      const observer: IntersectionObserver = new IntersectionObserver(
        entries => {
          entries.forEach(entry => {
            callback(entry)
          })
        },
        options
      )

      const video: HTMLVideoElement | null =
        videoContainer.querySelector('video')
      if (video) {
        observer.observe(video)
      }
    }

    const videoIds: string[] = [
      'about-video-container',
      'feature-1-video-container',
      'feature-2-video-container',
      'feature-3-video-container',
      'feature-4-video-container'
    ]

    videoIds.forEach((videoId: string) => {
      const videoContainer: HTMLElement | null =
        document.getElementById(videoId)
      if (videoContainer) {
        const videoControls: HTMLElement | null = videoContainer.querySelector(
          '.video-controls-container'
        )
        if (videoControls.classList.contains('d-none')) {
          return
        }
        const video: HTMLVideoElement | null =
          videoContainer.querySelector('video')
        const playIcon: HTMLElement | null = videoControls
          ? videoControls.querySelector(
              '.play-pause-button [data-state="play"]'
            )
          : null
        const pauseIcon: HTMLElement | null = videoControls
          ? videoControls.querySelector(
              '.play-pause-button [data-state="pause"]'
            )
          : null

        if (video && videoControls && playIcon && pauseIcon) {
          observeVideo(videoContainer)

          video.addEventListener('timeupdate', () => trackPlayback(video))
          videoControls.addEventListener('click', () =>
            togglePlay(video, playIcon, pauseIcon)
          )
        }
      }
    })
  }

  static init(): void {
    IndexController.usersCounter()
    IndexController.adaptiveLazyLoadImages()
    IndexController.handleVideosPlaybackTrack()
  }
}
